import { Check, X } from 'lucide-react'

/*
 * Factual comparison data — every claim traces to COMPETITOR_RESEARCH.md
 * (May 2026, re-verified June 2026). If a competitor improves, update here
 * and both pages update together. Cell values:
 *   { yes: true }              → lime check + optional note
 *   { yes: false }             → red X + optional note
 *   { partial: true, note }    → muted text (be fair: partial is not "no")
 */
export const COMPARISON_ROWS = [
  {
    feature: 'Reads bank statements (PDF/CSV)',
    autosplit: { yes: true, note: 'Chase, Amex, BofA, Citi, Capital One + any bank' },
    splitwise: { yes: false, note: 'declined the feature request' },
    tricount: { yes: false },
  },
  {
    feature: 'Receipt scanning',
    autosplit: { yes: true, note: 'free AI scan — camera or gallery' },
    splitwise: { partial: true, note: 'Pro only, camera only' },
    tricount: { partial: true, note: 'photo attach, no scan' },
  },
  {
    feature: 'Free expense limit',
    autosplit: { yes: true, note: 'unlimited' },
    splitwise: { yes: false, note: 'a few per day on free (as of 2026)' },
    tricount: { yes: true, note: 'unlimited' },
  },
  {
    feature: 'Ad-free, no upgrade nags',
    autosplit: { yes: true },
    splitwise: { yes: false, note: 'ads + countdown timers on free' },
    tricount: { yes: true, note: 'mostly' },
  },
  {
    feature: 'Bulk-edit many expenses at once',
    autosplit: { yes: true },
    splitwise: { yes: false, note: 'requested since 2012' },
    tricount: { yes: false },
  },
  {
    feature: 'Multi-currency conversion',
    autosplit: { yes: true, note: 'built in free — one rate per statement, not per expense' },
    splitwise: { partial: true, note: 'Pro only, not retroactive' },
    tricount: { partial: true, note: 'manual rate per expense' },
  },
  {
    feature: 'Friends join without an account',
    autosplit: { yes: true, note: 'settle links open in any browser' },
    splitwise: { yes: false, note: 'account + contact info required' },
    tricount: { yes: true, note: 'shareable link' },
  },
]

// One cell: lime check (we have it / good), red X (missing / hostile),
// or fair grey text for partial support.
function Cell({ value, highlight }) {
  const Icon = value.partial ? null : value.yes ? Check : X
  const iconColor = value.yes ? 'text-lime-400' : 'text-red-400/80'
  return (
    <td className={`px-3 py-3 align-top text-xs leading-relaxed ${highlight ? 'bg-lime-400/[0.04]' : ''}`}>
      <div className="flex items-start gap-1.5">
        {Icon && <Icon size={13} className={`${iconColor} flex-shrink-0 mt-0.5`} strokeWidth={2.5} />}
        <span className={value.partial ? 'text-ink-400' : value.yes ? 'text-ink-200' : 'text-ink-500'}>
          {value.partial ? value.note : value.note || (value.yes ? 'Yes' : 'No')}
        </span>
      </div>
    </td>
  )
}

export default function ComparisonTable() {
  return (
    <div className="overflow-x-auto rounded-2xl border border-ink-800">
      <table className="w-full min-w-[560px] text-left">
        <thead>
          <tr className="border-b border-ink-800 bg-ink-900/60">
            <th className="px-3 py-3 text-xs font-mono uppercase tracking-widest text-ink-500">Feature</th>
            <th className="px-3 py-3 text-sm font-semibold text-lime-400 bg-lime-400/[0.06]">AutoSplit</th>
            <th className="px-3 py-3 text-sm font-semibold text-ink-300">Splitwise</th>
            <th className="px-3 py-3 text-sm font-semibold text-ink-300">Tricount</th>
          </tr>
        </thead>
        <tbody>
          {COMPARISON_ROWS.map(row => (
            <tr key={row.feature} className="border-b border-ink-800/60 last:border-0">
              <td className="px-3 py-3 text-sm text-ink-200 font-medium align-top">{row.feature}</td>
              <Cell value={row.autosplit} highlight />
              <Cell value={row.splitwise} />
              <Cell value={row.tricount} />
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  )
}
